import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  viewChild,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DependantSelectionService } from '@core/form/dependant-selection.service';
import {
  NgbDropdown,
  NgbDropdownModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { matInfo } from '@ng-icons/material-icons/baseline';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { AiIndicatorComponent } from '@shared/components/boostrap-dropdown/ai-indicator/ai-indicator.component';
import { CustomAttributeValue } from '@shared/interfaces/attribute-description.interface';
import { FormUtils } from '@shared/utils/form-utils';

const HOVER_CLOSE_DELAY_MS = 150;

@Component({
  selector: 'app-boostrap-dropdown',
  templateUrl: './boostrap-dropdown.component.html',
  styleUrls: ['./boostrap-dropdown.component.scss'],
  imports: [
    NgIcon,
    NgClass,
    NgSelectModule,
    FormsModule,
    TranslateModule,
    NgbDropdownModule,
    AiIndicatorComponent,
    CommonModule,
    NgbTooltipModule,
  ],
  viewProviders: [provideIcons({ matInfo })],
})
export class BoostrapDropdownComponent implements OnChanges, OnDestroy {
  @Input() currentIndex = 0;
  @Input() editable = true;
  @Input() items: CustomAttributeValue[] = [];
  @Input() defaultLabel = 'Select Item';
  @Input() replaceAsStarKey = '';
  @Input() container = 'body';
  @Input() styleClass = '';
  @Input() dropdownImgClass = '';
  @Input() smartTyping = false;
  @Input() frequentlyUsedChoice: CustomAttributeValue[];
  @Input() masterChoiceId: string;
  @Input() placement = 'bottom-left';
  @Input() openOnHover = false;

  @Output() selectItem = new EventEmitter<CustomAttributeValue>();
  @Output() currentIndexChange = new EventEmitter<number>();

  @ViewChild('selectComp') selectComp: NgSelectComponent;

  dropdown = viewChild(NgbDropdown);

  unknownSelectionCodeId = '???';

  private closeTimer: ReturnType<typeof setTimeout>;

  selectedItem: CustomAttributeValue;
  customSearchFn = FormUtils.customSearchFn;

  placeholder = 'SmallFilter.Select';

  constructor(
    private readonly dependantSelectionService: DependantSelectionService
  ) {
    this.dependantSelectionService.getMasterChoiceId().subscribe(id => {
      if (this.masterChoiceId === id) {
        this.currentIndex = 0;
        this.selectItem.emit(this.items[0]);
        this.currentIndexChange.emit(this.currentIndex);

        setTimeout(() => {
          this.selectComp?.open();
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items && this.editable) {
      this.handleUnknownSelection();
      this.selectedItem = this.items[this.currentIndex];
    }
  }

  select(item: CustomAttributeValue, index?: number): void {
    this.currentIndex =
      index ?? this.items.findIndex(i => i.codeId === item.codeId);
    this.selectItem.emit(item);
    this.currentIndexChange.emit(this.currentIndex);
  }

  handleUnknownSelection(): void {
    const unknownSelectionIndex = this.items.findIndex(
      item => item.codeId === this.unknownSelectionCodeId
    );

    if (unknownSelectionIndex === -1) {
      return;
    }

    this.items = this.items.filter(
      item => item.codeId !== this.unknownSelectionCodeId
    );

    this.select(this.items[0], 0);
  }

  onSearch(value) {
    const { term } = value;
    if (term) {
      this.items = this.items.map(item => {
        if (item.group === 'Frequently used choices') {
          item.group = '';
        }
        return item;
      });
    } else {
      this.items = this.items.map(item => {
        const isFrequentChoice = this.isFrequentlyUsedChoice(item);
        if (isFrequentChoice) {
          item.group = 'Frequently used choices';
        }
        return item;
      });
    }
  }

  isFrequentlyUsedChoice(choice: CustomAttributeValue): boolean {
    return !!this.frequentlyUsedChoice.some(i => i.codeId === choice.codeId);
  }

  onFocus() {
    this.placeholder = '';
  }

  onEnterDropdown(): void {
    if (!this.openOnHover) {
      return;
    }

    clearTimeout(this.closeTimer);
    this.dropdown()?.open();
  }

  onLeaveDropdown(): void {
    if (!this.openOnHover) {
      return;
    }

    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(
      () => this.dropdown()?.close(),
      HOVER_CLOSE_DELAY_MS
    );
  }

  ngOnDestroy(): void {
    clearTimeout(this.closeTimer);
  }
}

export interface DropdownItemInterface {
  text: string;
  [key: string]: string;
}
